import {RemoteI9Details} from '@dtos/remote-i9-details';
import {ErrorTypes} from '@models/Errors';
import {IConfigurationValuesService} from '@services/ConfigurationValuesService';
import {RequireAuthenticationToken} from '@services/http/authorisation-interceptor';
import {IHttpService} from '@services/HttpService';
import type {RepresentativeDeclineRequest} from '@dtos/remote-i9-decline';
import type {ArchiveI9Documents, RepresentativeSection2SignRequest, RepresentativeSection3SignRequest} from '@dtos/remote-i9-sign';
import {RemoteI9StatusDetails} from '@dtos/remote-i9-status';
import { ReceiptDocumentDto } from '@dtos/receipt-documents';
import {I9DocumentInfoDto as LibraryDocumentInfo} from '@swipejobs/i9-document-validations-react';
 import { I9DocumentInfoDto } from '@dtos/I9Form';

export interface IRemoteRepresentativeService {
    getStatusAsync(): Promise<RemoteI9StatusDetails>;
    getDocsForReceipts(): Promise<LibraryDocumentInfo[]>;
    archiveI9Documents(request: ArchiveI9Documents): Promise<void>;
    getRemoteI9DetailsAsync(): Promise<RemoteI9Details>;
    getDocumentTypes( request: {documentTypes: string[];
      includeDisabled: boolean;
    }): Promise<LibraryDocumentInfo[]>;
    signI9Section3Async(request: RepresentativeSection3SignRequest): Promise<boolean>;
    signI9Section2Async(request: RepresentativeSection2SignRequest): Promise<boolean>;
    declineI9Async(request: RepresentativeDeclineRequest): Promise<boolean>;
    resetI9Async(): Promise<boolean>;
}

export enum RemoteRepresentativeEndpoint {
    remoteI9Details = '',
    signI9Section3 = '/sign',
    signI9Section2 = '/section2/sign',
    declineI9 = '/decline',
    status = '/status',
    resetI9 = '/documents/reset',
    receiptDocuments = '/document/validation/receipt',
    archiveDocuments = '/archive/section2/documents',
    documentTypes = '/document/validation/documentType'
}

export default class RemoteRepresentativeService implements IRemoteRepresentativeService {
    static readonly basePath: string = '/worker-onboarding/external/representative';

    constructor(
        private httpService: IHttpService,
        private configurationValuesService: IConfigurationValuesService
    ) {

    }

    get urlPrefix(): string {
        return `${this.configurationValuesService.apiUrl}${RemoteRepresentativeService.basePath}`;
    }

    private parseNotFoundError(error: any) {
        if (error.response?.status === 400 &&
            error.response?.data?.errorCode === ErrorTypes.NotFound) {
            throw new Error(ErrorTypes.NotFound);
        }
    }

    @RequireAuthenticationToken
    async getStatusAsync(): Promise<RemoteI9StatusDetails> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.status}`;
            const response = await this.httpService.get<RemoteI9StatusDetails>(url);
            return response.data;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async getDocsForReceipts(): Promise<LibraryDocumentInfo[]> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.receiptDocuments}`;
            const response = await this.httpService.get<LibraryDocumentInfo[]>(url);
            return response.data;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async archiveI9Documents(request: ArchiveI9Documents): Promise<void> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.archiveDocuments}`;
            await this.httpService.post(url, request);
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async getRemoteI9DetailsAsync(): Promise<RemoteI9Details> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.remoteI9Details}`;
            const response = await this.httpService.get<RemoteI9Details>(url);
            return response.data;

        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async signI9Section2Async(request: RepresentativeSection2SignRequest): Promise<boolean> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.signI9Section2}`;
            const response = await this.httpService.post(url, request);
            return response?.status === 200;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async signI9Section3Async(request: RepresentativeSection3SignRequest): Promise<boolean> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.signI9Section3}`;
            const response = await this.httpService.post(url, request);
            return response?.status === 200;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async declineI9Async(request: RepresentativeDeclineRequest): Promise<boolean> {
        try {
            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.declineI9}`;
            const response = await this.httpService.post(url, request);
            return response?.status === 200;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    @RequireAuthenticationToken
    async resetI9Async(): Promise<boolean> {
        try {
            // RSB-269 - for this specific scenario an extra flag for re-upload docs is required
            const enabledUploadDocuments = this.configurationValuesService.enabledUploadDocuments;
            const queryString = enabledUploadDocuments ? '?skipARReset=True' : '';

            const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.resetI9}${queryString}`;
            const response = await this.httpService.post(url);
            return response?.status === 200;
        } catch (error: unknown) {
            this.parseNotFoundError(error);
            throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
        }
    }

    async getDocumentTypes(request: {
      documentTypes: string[];
      includeDisabled: boolean;
    }): Promise<LibraryDocumentInfo[]> {
      try {
        const url = `${this.urlPrefix}${RemoteRepresentativeEndpoint.documentTypes}`;
        const response = await this.httpService.post(url, request);
        return response.data;
      } catch (error: unknown) {
        this.parseNotFoundError(error);
        throw new Error(`${ErrorTypes.ErrorOther} - ${(error as Error).message}`);
      }
    }
}



// WEBPACK FOOTER //
// ./src/services/remote-representative-service.ts